// Single shared WebSocket connection. Subscribers register callbacks via
// onLog / onProgress; the returned function unsubscribes (mirrors the
// preload contract).

import { API_BASE, API_KEY, getMediaToken } from './http.js';
import { IS_DESKTOP, LOCAL_API_KEY } from './runtime.js';
import { getDeviceId, notifySessionLost } from '../auth/session.js';

const logSubs = new Set();
const progressSubs = new Set();
let sock = null;
let reconnectTimer = null;

function wsUrl() {
  // The socket authenticates with ?token=<media token> and identifies itself
  // with ?device_id=<id> so the server can push an immediate session_revoked
  // when another device takes over the account. A WebSocket handshake cannot
  // carry an Authorization header, so it gets the scoped media token rather
  // than the Firebase ID token.
  const token = getMediaToken();
  const params = [];
  if (token) params.push(`token=${encodeURIComponent(token)}`);
  params.push(`device_id=${encodeURIComponent(getDeviceId())}`);
  // A WebSocket handshake cannot carry custom headers, so the API key rides in
  // the query string alongside the media token.
  // The event stream belongs to the server doing the work, so on desktop it
  // is the local server and the local key — the account server's key would be
  // rejected, which is exactly what happened the first time this ran.
  const wsKey = IS_DESKTOP ? LOCAL_API_KEY : API_KEY;
  if (wsKey) params.push(`api_key=${encodeURIComponent(wsKey)}`);
  const qs = `?${params.join('&')}`;
  if (!API_BASE) {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}/ws${qs}`;
  }
  const base = API_BASE.replace(/^http/, 'ws');
  return `${base}/ws${qs}`;
}

function connect() {
  if (sock && (sock.readyState === 0 || sock.readyState === 1)) return;
  try {
    sock = new WebSocket(wsUrl());
  } catch (e) {
    scheduleReconnect();
    return;
  }
  sock.onmessage = (ev) => {
    let msg;
    try { msg = JSON.parse(ev.data); } catch { return; }
    if (!msg || !msg.type) return;
    if (msg.type === 'log') {
      for (const cb of logSubs) { try { cb(msg); } catch {} }
    } else if (msg.type === 'progress') {
      for (const cb of progressSubs) { try { cb(msg); } catch {} }
    } else if (msg.type === 'session_revoked') {
      // Another device took over this account → force re-login immediately.
      notifySessionLost();
    }
  };
  sock.onclose = scheduleReconnect;
  sock.onerror = () => { try { sock.close(); } catch {} };
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, 1500);
}

export function ensureConnected() { connect(); }

// Force a fresh connection with the current token. Call after login (the
// initial connect at module-load has no token and is closed by the server)
// and after the act-as selection changes (so the superadmin's socket
// reflects the right context).
export function reconnect() {
  if (sock) { try { sock.close(); } catch {} sock = null; }
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  connect();
}
export function onLog(cb) {
  logSubs.add(cb);
  return () => logSubs.delete(cb);
}
export function onProgress(cb) {
  progressSubs.add(cb);
  return () => progressSubs.delete(cb);
}
